/**
 * Mobile-browser PWA hint: install when new, open / reinstall when already installed.
 * Non-blocking bar above the footer (or screen bottom on public pages).
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  dismissPwaBannerForSession,
  isBeforeInstallPromptEvent,
  isIosSafari,
  isMobileBrowser,
  isPwaBannerDismissedForSession,
  isPwaInstalled,
  markPwaInstalledOnDevice,
  wasPwaInstalledOnDevice,
  type BeforeInstallPromptEvent,
} from '../lib/pwaInstall'
import { Button } from './ui'

export function PwaInstallPrompt() {
  const { t } = useTranslation()
  const barRef = useRef<HTMLDivElement>(null)
  const [mobile] = useState(isMobileBrowser)
  const [installed, setInstalled] = useState(isPwaInstalled)
  const [dismissed, setDismissed] = useState(isPwaBannerDismissedForSession)
  const [hadInstall, setHadInstall] = useState(wasPwaInstalledOnDevice)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    null,
  )
  const [busy, setBusy] = useState(false)

  const visible = mobile && !installed && !dismissed

  useLayoutEffect(() => {
    const root = document.documentElement
    const el = barRef.current
    if (!visible || !el) {
      root.style.setProperty('--app-pwa-banner-height', '0px')
      return
    }
    const sync = () => {
      root.style.setProperty('--app-pwa-banner-height', `${el.offsetHeight}px`)
    }
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.setProperty('--app-pwa-banner-height', '0px')
    }
  }, [visible, hadInstall, deferredPrompt])

  useEffect(() => {
    const onInstallable = (event: Event) => {
      if (!isBeforeInstallPromptEvent(event)) return
      event.preventDefault()
      setDeferredPrompt(event)
    }
    const onInstalled = () => {
      markPwaInstalledOnDevice()
      setHadInstall(true)
      setInstalled(true)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', onInstallable)
    window.addEventListener('appinstalled', onInstalled)

    const mq = window.matchMedia('(display-mode: standalone)')
    const onDisplayChange = () => setInstalled(isPwaInstalled())
    mq.addEventListener('change', onDisplayChange)

    return () => {
      window.removeEventListener('beforeinstallprompt', onInstallable)
      window.removeEventListener('appinstalled', onInstalled)
      mq.removeEventListener('change', onDisplayChange)
    }
  }, [])

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return
    setBusy(true)
    try {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      setDeferredPrompt(null)
      if (choice.outcome === 'accepted') {
        markPwaInstalledOnDevice()
        setHadInstall(true)
      }
      setInstalled(isPwaInstalled())
    } finally {
      setBusy(false)
    }
  }, [deferredPrompt])

  const handleDismiss = useCallback(() => {
    dismissPwaBannerForSession()
    setDismissed(true)
  }, [])

  if (!visible) return null

  const ios = isIosSafari()
  const canNativeInstall = Boolean(deferredPrompt)
  const returning = hadInstall

  const titleKey = returning ? 'pwa.titleOpen' : 'pwa.titleInstall'
  const bodyKey = returning
    ? ios
      ? 'pwa.openIosBody'
      : 'pwa.openBody'
    : ios
      ? 'pwa.iosInstallBody'
      : canNativeInstall
        ? 'pwa.installBody'
        : 'pwa.mobileMenuHint'

  return (
    <div
      ref={barRef}
      className="pwa-install-bar fixed inset-x-0 z-[39] border-t border-brand-800/15 bg-brand-50/95 px-3 py-2.5 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] backdrop-blur-md md:hidden"
      role="region"
      aria-label={t(titleKey)}
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-stone-900">{t(titleKey)}</p>
          <p className="text-xs text-stone-600">{t(bodyKey)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {returning ? (
            <>
              {canNativeInstall ? (
                <Button
                  type="button"
                  className="px-4 py-2 text-sm"
                  onClick={() => void handleInstall()}
                  disabled={busy}
                >
                  {t('pwa.reinstall')}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                className="px-4 py-2 text-sm"
                onClick={handleDismiss}
              >
                {t('pwa.continueInBrowser')}
              </Button>
            </>
          ) : (
            <>
              {canNativeInstall ? (
                <Button
                  type="button"
                  className="px-4 py-2 text-sm"
                  onClick={() => void handleInstall()}
                  disabled={busy}
                >
                  {t('pwa.install')}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                className="px-4 py-2 text-sm"
                onClick={handleDismiss}
              >
                {t('pwa.continueInBrowser')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
